import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiMail } from 'react-icons/fi';
import api from '../../services/api';
import toast from 'react-hot-toast';

const NewsletterPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState('/logo.png');

  useEffect(() => {
    // Check if already shown in this session
    const hasBeenShown = sessionStorage.getItem('newsletterPopupShown');
    if (hasBeenShown === 'true') {
      return;
    }

    const fetchLogo = async () => {
      try {
        const res = await api.get('/settings/announcement');
        if (res.logoUrl) setLogoUrl(res.logoUrl);
      } catch (err) {
        console.error('Failed to fetch logo', err);
      }
    };
    fetchLogo();

    const timer = setTimeout(() => {
      setIsOpen(true);
      sessionStorage.setItem('newsletterPopupShown', 'true');
    }, 30000);

    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }

    setLoading(true);
    try {
      await api.post('/newsletter/subscribe', { email });
      toast.success('Thank you for subscribing!');
      setIsOpen(false);
      setEmail('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to subscribe');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[99999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-8 relative animate-fade-in">
        <button
          onClick={() => setIsOpen(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
        >
          <FiX size={24} />
        </button>

        <div className="text-center">
          <img src={logoUrl} alt="Logo" className="h-16 mx-auto mb-4 object-contain" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Join Our Newsletter</h2>
          <p className="text-gray-600 mb-6">Get exclusive offers, new arrivals, and updates delivered to your inbox.</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[var(--primary-color)] hover:bg-[var(--hover-primary)] text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
          <p className="text-xs text-gray-400 mt-4">You can unsubscribe anytime.</p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NewsletterPopup;